import { useMemo, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Thumbs, Keyboard, A11y, FreeMode } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/thumbs';
import 'swiper/css/free-mode';
import './profile-gallery.css';

/**
 * Galería principal del perfil de una modelo.
 *
 * Visor grande con miniaturas de navegación. Las fotos llegan del backend con
 * proporciones desconocidas, así que la imagen se muestra completa
 * (`object-fit: contain`) sobre una copia difuminada de sí misma: así se llena
 * el marco sin recortar nada del contenido promocional.
 */
export default function ProfileGallery({ photos = [], name, onOpen }) {
    const [thumbsSwiper, setThumbsSwiper] = useState(null);
    const [activo, setActivo] = useState(0);

    // El bucle de Swiper necesita suficientes diapositivas; con una o dos
    // duplica slides y la navegación se comporta de forma errática.
    const conBucle = photos.length > 2;

    const actual = photos[activo];
    const pie = useMemo(
        () => actual?.title || actual?.description || null,
        [actual]
    );

    if (!photos.length) {
        return (
            <p className="public-muted profile-gallery-empty">
                Esta modelo aún no ha publicado fotos.
            </p>
        );
    }

    return (
        <section className="profile-gallery" aria-label={`Galería de ${name}`}>
            <Swiper
                modules={[Navigation, Pagination, Thumbs, Keyboard, A11y]}
                spaceBetween={12}
                slidesPerView={1}
                loop={conBucle}
                navigation
                keyboard={{ enabled: true }}
                pagination={{ type: 'fraction' }}
                thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
                onSlideChange={(swiper) => setActivo(swiper.realIndex)}
                className="profile-gallery-main"
            >
                {photos.map((photo) => (
                    <SwiperSlide key={photo.id}>
                        <button
                            type="button"
                            className="profile-gallery-slide"
                            onClick={() => onOpen?.(photo)}
                            title="Ver en grande"
                        >
                            {/* Fondo difuminado: rellena el marco sin recortar la foto */}
                            <span
                                className="profile-gallery-backdrop"
                                style={{ backgroundImage: `url(${photo.src})` }}
                                aria-hidden="true"
                            />
                            <img src={photo.src} alt={photo.alt} loading="lazy" />
                            <span className="profile-gallery-zoom" aria-hidden="true">
                                <i className="fa-solid fa-expand"></i>
                            </span>
                        </button>
                    </SwiperSlide>
                ))}
            </Swiper>

            {pie && <p className="profile-gallery-caption">{pie}</p>}

            {photos.length > 1 && (
                <Swiper
                    modules={[Thumbs, FreeMode, A11y]}
                    onSwiper={setThumbsSwiper}
                    spaceBetween={8}
                    slidesPerView="auto"
                    freeMode
                    watchSlidesProgress
                    className="profile-gallery-thumbs"
                >
                    {photos.map((photo) => (
                        <SwiperSlide key={`thumb-${photo.id}`}>
                            <img src={photo.src} alt="" loading="lazy" />
                        </SwiperSlide>
                    ))}
                </Swiper>
            )}
        </section>
    );
}
